/**
 * AdminStats Component
 * 
 * Dashboard overview with key metrics and statistics
 * Styled to match the main dashboard brand
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H2, H3, Text } from '../../../common/Typography';
import { 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  Users, 
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Activity,
  CreditCard
} from 'lucide-react';
import { paymentService } from '../../../services/api/paymentService';
import { adminService } from '../../../services/api/adminService';
import { useAnalyticsQuery } from '../../../hooks/queries/admin';

const AdminStats = () => {
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  
  const { data: analyticsData } = useAnalyticsQuery();

  useEffect(() => {
    fetchRecentPayments();
    fetchSystemHealth();
  }, []);
  
  const fetchSystemHealth = async () => {
    try {
      setHealthLoading(true);
      const health = await adminService.getSystemHealth();
      setSystemHealth(health);
    } catch (error) {
      console.error('Error fetching system health:', error);
      // Set fallback data on error
      setSystemHealth({
        overall: 'warning',
        overallMessage: 'Unable to fetch health status',
        services: []
      });
    } finally {
      setHealthLoading(false);
    }
  };
  
  const fetchRecentPayments = async () => {
    try {
      setLoading(true);
      const payments = await paymentService.getAllPayments();
      // Get the 3 most recent payments
      const recent = payments
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 3);
      setRecentPayments(recent);
    } catch (error) {
      console.error('Error fetching recent payments:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    monthlyPayments: 0,
    monthlyAmount: 0,
    totalUsers: 0,
    totalApplications: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (analyticsData && analyticsData.summary) {
      setStats(prev => ({
        ...prev,
        totalUsers: analyticsData.summary.totalUsers || 0,
        totalApplications: analyticsData.summary.totalApplications || 0
      }));
    }
  }, [analyticsData]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const paymentStats = await paymentService.getPaymentStats();
      
      // Update payment stats while preserving other stats (like those from analytics)
      setStats(prev => ({
        ...prev,
        totalPayments: paymentStats.totalPayments || 0,
        totalAmount: paymentStats.totalAmount || 0,
        monthlyPayments: paymentStats.monthlyPayments || 0,
        monthlyAmount: paymentStats.monthlyAmount || 0
      }));
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError('Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount / 100); // Convert from cents
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <H3 className="mb-2 text-red-900">Error Loading Stats</H3>
          <Text className="text-gray-600 mb-4">{error}</Text>
          <button
            onClick={fetchStats}
            className="bg-[#004d40] hover:bg-[#00695c] text-white px-4 py-2 rounded-md"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  // Get real change values from analytics data
  const revenueChange = analyticsData?.summary?.revenueChange || '+0%';
  const monthlyRevenueChange = analyticsData?.summary?.monthlyRevenueChange || '+0%';
  const userChange = analyticsData?.summary?.userChange || '+0%';
  const appChange = analyticsData?.summary?.appChange || '+0%';

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(stats.totalAmount),
      change: revenueChange,
      changeType: revenueChange.startsWith('-') ? 'negative' : 'positive',
      icon: DollarSign,
      subtitle: `From ${stats.totalPayments} payments`
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(stats.monthlyAmount),
      change: monthlyRevenueChange,
      changeType: monthlyRevenueChange.startsWith('-') ? 'negative' : 'positive',
      icon: TrendingUp,
      subtitle: `From ${stats.monthlyPayments} payments this month`
    },
    {
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      change: userChange,
      changeType: userChange.startsWith('-') ? 'negative' : 'positive',
      icon: Users,
      subtitle: 'Registered users'
    },
    {
      title: 'Applications',
      value: stats.totalApplications.toLocaleString(),
      change: appChange,
      changeType: appChange.startsWith('-') ? 'negative' : 'positive',
      icon: FileText,
      subtitle: 'Trademark applications'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const isPositive = stat.changeType === 'positive';
          const ChangeIcon = isPositive ? TrendingUp : TrendingDown;
          
          // Different accent colors for each card
          const colors = [
            { bg: 'bg-[#e0f2f1]', icon: 'text-[#00796b]', ring: 'ring-[#00796b]/20' },
            { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-500/20' },
            { bg: 'bg-cyan-50', icon: 'text-cyan-600', ring: 'ring-cyan-500/20' },
            { bg: 'bg-purple-50', icon: 'text-purple-600', ring: 'ring-purple-500/20' },
          ];
          const color = colors[index % colors.length];
          
          return (
            <div 
              key={index}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {stat.title}
                  </p>
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                      isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      <ChangeIcon className="h-3 w-3" />
                      {stat.change}
                    </div>
                    <span className="text-xs text-gray-400">vs last month</span>
                  </div>
                </div>
                <div className={`${color.bg} p-3 rounded-xl ring-4 ${color.ring}`}>
                  <Icon className={`h-5 w-5 ${color.icon}`} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                {stat.subtitle}
              </p>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payments */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#e0f2f1] rounded-lg">
                <CreditCard className="w-4 h-4 text-[#00796b]" />
              </div>
              <h3 className="font-semibold text-gray-900">Recent Payments</h3>
            </div>
            <button className="text-sm text-[#00796b] hover:text-[#004d40] font-medium">
              View all
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00796b] border-t-transparent mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Loading payments...</p>
              </div>
            ) : recentPayments.length > 0 ? (
              recentPayments.map((payment) => (
                <div key={payment.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      payment.refunded ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      {payment.refunded ? (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">#{payment.id.slice(-8)}</p>
                      <p className="text-xs text-gray-500">{formatDate(payment.createdAt)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${payment.refunded ? 'text-red-600' : 'text-green-600'}`}>
                      {payment.refunded ? '-' : '+'}{formatAmount(payment.refunded ? payment.refundAmount : payment.amount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {payment.refunded ? 'Refunded' : 'Succeeded'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No recent payments</p>
              </div>
            )}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-emerald-50 rounded-lg">
                <Activity className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900">System Health</h3>
            </div>
            {healthLoading ? (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                Checking...
              </span>
            ) : (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                systemHealth?.overall === 'healthy' ? 'bg-green-100 text-green-700' :
                systemHealth?.overall === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {systemHealth?.overallMessage || 'Unknown'}
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {healthLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00796b] border-t-transparent mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Checking system health...</p>
              </div>
            ) : systemHealth?.services?.length > 0 ? (
              <>
                {/* API Response Time */}
                {systemHealth.apiResponseTime && (
                  <div className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500">
                        <div className="w-full h-full rounded-full bg-green-500 animate-ping opacity-75"></div>
                      </div>
                      <span className="text-sm text-gray-700">API Response Time</span>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      {systemHealth.apiResponseTime}
                    </span>
                  </div>
                )}
                {/* Service statuses */}
                {systemHealth.services.map((service, idx) => (
                  <div key={idx} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${
                        service.status === 'healthy' ? 'bg-green-500' : 
                        service.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}>
                        {service.status === 'healthy' && (
                          <div className="w-full h-full rounded-full bg-green-500 animate-ping opacity-75"></div>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{service.name}</span>
                    </div>
                    <span className={`text-sm font-medium ${
                      service.status === 'healthy' ? 'text-green-600' : 
                      service.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {service.value}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Unable to fetch health status</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
